/**
 * Labstep
 *
 * @module prosemirror/extensions/paste
 * @desc Handle paste
 */

import type { Experiment } from 'labstep-web/models/experiment.model';
import { File } from 'labstep-web/models/file.model';
import type { Protocol } from 'labstep-web/models/protocol.model';
import { NodeFile } from 'labstep-web/prosemirror/nodes/file';
import { uploadFile } from 'labstep-web/state/actions/file';
import { thumbnailFetchUrlAction } from 'labstep-web/state/actions/thumbnail';
import store from 'labstep-web/state/store';
import type { EditorState } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';

export interface FileWithPosition {
  file: any;
  position?: number;
}

export const onCreateFilesSuccess = (
  state: EditorState,
  dispatch,
  uploadedFiles: File[],
  positions?: number[],
) => {
  const { tr } = state;
  tr.setStoredMarks([]);

  // If positions are provided, insert at specific positions
  // Otherwise, insert at cursor position
  if (positions && positions.length === uploadedFiles.length) {
    // Sort by position in reverse order to maintain correct positions as we insert
    const filesWithPositions = uploadedFiles
      .map((file, index) => ({
        file,
        position: positions[index],
      }))
      .sort((a, b) => b.position - a.position);

    filesWithPositions.forEach(({ file, position }) => {
      const node = state.schema.nodes[NodeFile.key].create({
        id: file.id,
      });
      tr.insert(position, node);
    });
  } else {
    let currentPosition = tr.selection.$to.pos;
    uploadedFiles.forEach((file) => {
      const node = state.schema.nodes[NodeFile.key].create({
        id: file.id,
      });
      tr.insert(currentPosition, node);
      currentPosition = tr.selection.$to.pos;
    });
  }

  dispatch(tr);
  uploadedFiles.forEach((file) => {
    if (File.getIsImage(file.name, file.mime_type)) {
      store.dispatch(
        thumbnailFetchUrlAction(file.guid, { h: 1280, w: 1280 }, {}),
      );
    }
  });
};

export const createFiles = (
  state: EditorState,
  dispatch: any,
  entity: Experiment | Protocol,
  files: any,
  view?: EditorView,
  positions?: number[],
): void => {
  // Capture the length immediately as FileList.length can become 0 in async callbacks in Firefox
  const totalFiles = files.length;
  const uploadedFiles: File[] = [];

  // Convert FileList to array to avoid closure issues
  const filesArray = Array.from(files);

  for (let i = 0; i < totalFiles; i++) {
    // Capture the file in an IIFE to avoid closure issues
    ((fileToUpload: any, index) => {
      store.dispatch(
        uploadFile(
          {
            file: fileToUpload,
            entityType: entity.entityName,
            entityId: entity.id,
          },
          {
            onSuccess: ({ response }) => {
              const id = (response as any).result[0];
              const uploadedFile: File = (response as any).entities
                .file[id];
              uploadedFiles.push(uploadedFile);
              if (uploadedFiles.length === totalFiles) {
                // Use current view state if available, otherwise use captured state
                const currentState = view ? view.state : state;
                const currentDispatch = view
                  ? view.dispatch
                  : dispatch;
                onCreateFilesSuccess(
                  currentState,
                  currentDispatch,
                  uploadedFiles,
                  positions,
                );
              }
            },
          },
        ),
      );
    })(filesArray[i], i);
  }
};
